import {
  AstroCityEntry,
  AstroCityHit,
  AstroFeature,
  AstroFeatureProperties,
  AstroLine,
  AstroLineCategory,
} from "../../../../models/interfaces";
import {
  ANGLE_NORMALISATION,
  ANY_ANGLE,
  FALLBACK_LINE_COLOR,
  KM_PER_DEGREE,
  LABEL_CHAR_WIDTH,
  LABEL_PADDING_X,
  MAP_MIN_ZOOM,
  PLANET_GLYPHS,
  PLANET_ORDER,
  WORLD_WIDTH_DEG,
  ZOOM_WEIGHT_MAX,
  ZOOM_WEIGHT_MIN,
  ZOOM_WEIGHT_STEP,
} from "./astrocartographyConstants";

export const getPlanetGlyph = (planet: string) => PLANET_GLYPHS[planet] ?? "";

/** Main lines carry a `line` property, aspect lines do not — the payload discriminator. */
export const isMainLine = (properties: AstroFeatureProperties) =>
  Boolean(properties.line);

export const normaliseAngle = (angle?: string) =>
  angle ? (ANGLE_NORMALISATION[angle] ?? angle) : "";

export const getLineCategory = (
  properties: AstroFeatureProperties,
): AstroLineCategory => {
  if (isMainLine(properties)) return "main";
  return properties.harmony === "harmonious" ? "harm" : "disharm";
};

/**
 * Human-readable line name, shown on hover tooltips and on the end-of-line badges.
 * Aspect features already ship a composed `label` (e.g. "Sun △ AC").
 */
export const getLineLabel = (properties: AstroFeatureProperties) => {
  const glyph = getPlanetGlyph(properties.planet);
  if (isMainLine(properties)) {
    return `${glyph} ${properties.planet} ${properties.line}`.trim();
  }
  return `${glyph} ${properties.label ?? properties.planet}`.trim();
};

/** GeoJSON gives [lon, lat]; Leaflet wants [lat, lon]. */
export const toLatLngs = (coordinates: number[][]): [number, number][] =>
  coordinates.map((coordinate) => [coordinate[1], coordinate[0]]);

/** Turns the raw feature collection into the flat line model the map renders. */
export const buildLines = (features: AstroFeature[]): AstroLine[] =>
  features
    .filter((feature) => feature?.geometry?.coordinates?.length)
    .map((feature) => {
      const properties = feature.properties;
      return {
        planet: properties.planet,
        category: getLineCategory(properties),
        kind: isMainLine(properties)
          ? (properties.line as string)
          : (properties.angle ?? ""),
        label: getLineLabel(properties),
        coords: toLatLngs(feature.geometry.coordinates),
        properties,
      };
    });

/** Planets present in the payload, ordered the way the reference UI lists them. */
export const collectPlanets = (lines: AstroLine[]) => {
  const planets: string[] = [];
  lines.forEach((line) => {
    if (!planets.includes(line.planet)) planets.push(line.planet);
  });
  return planets.sort(
    (a, b) => PLANET_ORDER.indexOf(a) - PLANET_ORDER.indexOf(b),
  );
};

/** First colour seen per planet — used for toggles, badges and city markers. */
export const collectPlanetColors = (lines: AstroLine[]) => {
  const colors: Record<string, string> = {};
  lines.forEach((line) => {
    if (!(line.planet in colors)) {
      colors[line.planet] = line.properties.color ?? FALLBACK_LINE_COLOR;
    }
  });
  return colors;
};

/** Main-line segments grouped per planet — the search space for the city lookup. */
export const groupMainSegmentsByPlanet = (lines: AstroLine[]) => {
  const grouped: Record<string, AstroLine[]> = {};
  lines.forEach((line) => {
    if (line.category !== "main") return;
    grouped[line.planet] = grouped[line.planet] ?? [];
    grouped[line.planet].push(line);
  });
  return grouped;
};

/** Leaflet stroke style for a line, matching the reference rendering hints. */
export const getLineStyle = (line: AstroLine) => {
  const color = line.properties.color ?? FALLBACK_LINE_COLOR;
  if (line.category !== "main") {
    return { color, weight: 2.2, opacity: 0.65, dashArray: "3,5" };
  }
  const isStrong = line.kind === "MC" || line.kind === "ASC";
  const isMeridian = line.kind === "MC" || line.kind === "IC";
  return {
    color,
    weight: isStrong ? 4.5 : 3,
    opacity: isStrong ? 0.95 : 0.85,
    dashArray: isMeridian ? undefined : "8,6",
  };
};

/** Black or white text, whichever stays readable on the given background. */
export const getContrastingTextColor = (hex: string) => {
  const value = hex.replace("#", "");
  if (value.length < 6) return "#ffffff";
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#151515" : "#ffffff";
};

/** Northern-most (or southern-most) point of a line — where its badge is anchored. */
export const getExtremePoint = (
  coords: [number, number][],
  northern: boolean,
): [number, number] => {
  let best = coords[0];
  coords.forEach((coord) => {
    if (northern ? coord[0] > best[0] : coord[0] < best[0]) best = coord;
  });
  return best;
};

/** Equirectangular point-to-segment distance in kilometres. */
const pointToSegmentKm = (
  pointLat: number,
  pointLon: number,
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
) => {
  const latitudeScale = Math.cos((pointLat * Math.PI) / 180);
  const px = pointLon * latitudeScale;
  const py = pointLat;
  const ax = aLon * latitudeScale;
  const ay = aLat;
  const bx = bLon * latitudeScale;
  const by = bLat;
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  let t = lengthSquared ? ((px - ax) * dx + (py - ay) * dy) / lengthSquared : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy)) * KM_PER_DEGREE;
};

/** Shortest distance from a city to any segment of a polyline, in kilometres. */
export const getCityToLineKm = (
  latitude: number,
  longitude: number,
  coords: [number, number][],
) => {
  let best = Infinity;
  for (let index = 1; index < coords.length; index += 1) {
    const distance = pointToSegmentKm(
      latitude,
      longitude,
      coords[index - 1][0],
      coords[index - 1][1],
      coords[index][0],
      coords[index][1],
    );
    if (distance < best) best = distance;
  }
  return best;
};

/**
 * Cities within `radiusKm` of the selected planet's line(s), nearest first.
 * Each hit reports the angle of the line it sits closest to.
 */
export const findCitiesNearLines = (
  cities: AstroCityEntry[],
  segments: AstroLine[],
  radiusKm: number,
): AstroCityHit[] => {
  const hits: AstroCityHit[] = [];
  cities.forEach(([name, countryCode, latitude, longitude]) => {
    let best = Infinity;
    let bestAngle = "";
    segments.forEach((segment) => {
      const distance = getCityToLineKm(latitude, longitude, segment.coords);
      if (distance < best) {
        best = distance;
        bestAngle = segment.kind;
      }
    });
    if (best <= radiusKm) {
      hits.push({
        name,
        countryCode,
        latitude,
        longitude,
        distanceKm: best,
        angle: bestAngle,
      });
    }
  });
  return hits.sort((a, b) => a.distanceKm - b.distanceKm);
};

/** Main-line segments of a planet, narrowed to one angle unless "any" is selected. */
export const selectSegments = (
  segmentsByPlanet: Record<string, AstroLine[]>,
  planet: string,
  angle: string,
) => {
  const segments = segmentsByPlanet[planet] ?? [];
  if (angle === ANY_ANGLE) return segments;
  return segments.filter((segment) => segment.kind === angle);
};

/** The response ships `en`/`pl`; fall back so a popup never renders empty. */
export const pickInterpretation = (
  properties: AstroFeatureProperties,
  language: string,
) => {
  const interpretations = properties.interpretation;
  if (!interpretations) return undefined;
  const normalised = language?.toLowerCase().split("-")[0];
  return (
    interpretations[normalised] ??
    interpretations.en ??
    interpretations[Object.keys(interpretations)[0]]
  );
};

/* ------------------------- world-wrapping helpers ------------------------- */

/**
 * Shifts a line into another copy of the world. The payload is computed once in
 * the -180…180 range, so drawing it in the neighbouring copies is what lets the
 * map wrap endlessly instead of panning into an empty Earth.
 */
export const shiftCoords = (
  coords: [number, number][],
  offsetLon: number,
): [number, number][] =>
  offsetLon === 0
    ? coords
    : coords.map(([lat, lon]) => [lat, lon + offsetLon] as [number, number]);

/** Which copy of the world a longitude falls in — 0 is the canonical one. */
export const getWorldIndex = (longitude: number) =>
  Math.round(longitude / WORLD_WIDTH_DEG);

/** Stroke multiplier for the current zoom, so lines gain weight as you zoom in. */
export const getZoomWeightScale = (zoom: number) => {
  const scale = ZOOM_WEIGHT_MIN + (zoom - MAP_MIN_ZOOM) * ZOOM_WEIGHT_STEP;
  return Math.max(ZOOM_WEIGHT_MIN, Math.min(ZOOM_WEIGHT_MAX, scale));
};

/** Rough on-screen width of a line label, for greedy collision avoidance. */
export const getLabelWidth = (label: string) =>
  label.length * LABEL_CHAR_WIDTH + LABEL_PADDING_X;
